import React, { useState, createContext, useContext, useCallback } from 'react';

interface AccordionContextType {
  expandedItems: string[];
  toggleItem: (itemId: string) => void;
  isItemExpanded: (itemId: string) => boolean;
}

const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

export interface AccordionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Default expanded item IDs */
  defaultExpandedItems?: string[];
  /** Whether multiple items can be expanded at once */
  allowMultiple?: boolean;
  /** Whether all items can be collapsed */
  allowAllClosed?: boolean;
  /** Called when the expanded state changes */
  onChange?: (expandedItems: string[]) => void;
  /** Visual variant of the accordion */
  variant?: 'default' | 'bordered' | 'separated';
  /** Size of the accordion */
  size?: 'sm' | 'md' | 'lg';
}

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Unique ID for the accordion item */
  itemId: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Visual variant passed from parent Accordion */
  variant?: 'default' | 'bordered' | 'separated';
  /** Size passed from parent Accordion */
  size?: 'sm' | 'md' | 'lg';
}

export interface AccordionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Item ID passed from parent AccordionItem */
  itemId?: string;
  /** Whether the accordion is expanded */
  expanded?: boolean;
  /** Whether the accordion is disabled */
  disabled?: boolean;
  /** Size passed from parent Accordion */
  size?: 'sm' | 'md' | 'lg';
}

export interface AccordionPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the accordion is expanded */
  expanded?: boolean;
  /** Size passed from parent Accordion */
  size?: 'sm' | 'md' | 'lg';
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      defaultExpandedItems = [],
      allowMultiple = false,
      allowAllClosed = true,
      onChange,
      variant = 'default',
      size = 'md',
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const [expandedItems, setExpandedItems] = useState<string[]>(defaultExpandedItems);
    
    const toggleItem = useCallback((itemId: string) => {
      setExpandedItems(prevExpandedItems => {
        // Check if item is already expanded
        const isExpanded = prevExpandedItems.includes(itemId);
        
        let newExpandedItems: string[];
        
        if (isExpanded) {
          // If all items can be closed or there are multiple items expanded
          if (allowAllClosed || prevExpandedItems.length > 1) {
            newExpandedItems = prevExpandedItems.filter(id => id !== itemId);
          } else {
            // Can't close the item if it's the only one open and allowAllClosed is false
            newExpandedItems = prevExpandedItems;
          }
        } else {
          // If multiple items can be expanded, add to existing array
          if (allowMultiple) {
            newExpandedItems = [...prevExpandedItems, itemId];
          } else {
            // Replace with just the new item
            newExpandedItems = [itemId];
          }
        }
        
        // Call onChange if provided
        if (onChange) {
          onChange(newExpandedItems);
        }
        
        return newExpandedItems;
      });
    }, [allowMultiple, allowAllClosed, onChange]);
    
    const isItemExpanded = useCallback((itemId: string) => {
      return expandedItems.includes(itemId);
    }, [expandedItems]);
    
    const variantClasses = {
      default: 'border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden',
      bordered: 'border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700',
      separated: 'space-y-2',
    };
    
    return (
      <AccordionContext.Provider value={{ expandedItems, toggleItem, isItemExpanded }}>
        <div
          ref={ref}
          className={`${variantClasses[variant]} ${className}`}
          {...props}
        >
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<any>, {
                variant,
                size,
              });
            }
            return child;
          })}
        </div>
      </AccordionContext.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';

export const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      itemId,
      disabled = false,
      children,
      className = '',
      variant = 'default',
      size = 'md',
      ...props
    },
    ref
  ) => {
    const context = useContext(AccordionContext);
    
    if (!context) {
      throw new Error('AccordionItem must be used within an Accordion');
    }
    
    const { isItemExpanded } = context;
    const expanded = isItemExpanded(itemId);
    
    const variantClasses = {
      default: '',
      bordered: '',
      separated: 'border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden',
    };
    
    return (
      <div
        ref={ref}
        className={`
          ${variantClasses[variant]} 
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
          ${className}
        `}
        {...props}
      >
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, {
              itemId,
              expanded,
              disabled,
              size,
            });
          }
          return child;
        })}
      </div>
    );
  }
);

AccordionItem.displayName = 'AccordionItem';

export const AccordionHeader = React.forwardRef<HTMLDivElement, AccordionHeaderProps>(
  (
    {
      children,
      className = '',
      itemId,
      expanded = false,
      disabled = false,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const context = useContext(AccordionContext);
    
    if (!context) {
      throw new Error('AccordionHeader must be used within an Accordion');
    }
    
    const { toggleItem } = context;
    
    const sizeClasses = {
      sm: 'text-sm py-2 px-3',
      md: 'text-base py-3 px-4',
      lg: 'text-lg py-4 px-5',
    };
    
    const handleClick = () => {
      if (!disabled) {
        toggleItem(itemId as string);
      }
    };
    
    return (
      <div
        ref={ref}
        className={`
          flex justify-between items-center
          ${sizeClasses[size]}
          cursor-pointer select-none
          ${disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}
          font-medium
          text-gray-900 dark:text-gray-100
          ${className}
        `}
        onClick={handleClick}
        aria-expanded={expanded}
        aria-disabled={disabled}
        role="button"
        tabIndex={disabled ? -1 : 0}
        {...props}
      >
        <div>{children}</div>
        <div className="ml-2 transition-transform duration-200 transform text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    );
  }
);

AccordionHeader.displayName = 'AccordionHeader';

export const AccordionPanel = React.forwardRef<HTMLDivElement, AccordionPanelProps>(
  (
    {
      children,
      className = '',
      expanded = false,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-5',
    };
    
    return (
      <div
        ref={ref}
        className={`
          overflow-hidden transition-all duration-200
          ${expanded ? 'max-h-96' : 'max-h-0'}
        `}
      >
        {expanded && (
          <div
            className={`
              border-t border-gray-200 dark:border-gray-700
              text-gray-700 dark:text-gray-300
              ${sizeClasses[size]}
              ${className}
            `}
            {...props}
          >
            {children}
          </div>
        )}
      </div>
    );
  }
);

AccordionPanel.displayName = 'AccordionPanel';

// Export as a namespace object for convenient usage
const AccordionNamespace = Object.assign(Accordion, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Panel: AccordionPanel,
});

export default AccordionNamespace;